import * as React from "react";
import { connect, ConnectedProps } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";

import { DEFAULT_TRANSLATABLE_CONTENT, ContentAnalysis as IContentAnalysis, TranslatedContent as ITranslatedContent, TranslatableContent } from "./models";
import ContentAnalysis from "./dialogs/contentAnalysis";
import { Body } from "./body";
import Summary from "./summary";
import Title from "./title";
import LanguageBarWithTranslateDialog from "../languageBarWithTranslateDialog";
import useLanguages from "modules/common/hooks/data/useLanguages";
import TranslateAllIssueDialog from "modules/settings/components/translateAllIssueDialog";
import { defaultTranslateContentId, ILanguage, ILcidTranslateDetails } from "modules/localization/models";
import { ICustomCssModelv1 } from "api/files";
import { TranslationContentType } from "modules/common/models";
import { cognitiveServicesApi, draftPostsApi } from "../../../../api/instances";

import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import TimelineIcon from "@mui/icons-material/Timeline";
import LoadingOverlay from "../loadingOverlay";
import useTranslateAll from "modules/common/hooks/data/useTranslateAll";
import { KeyboardArrowDown } from "@mui/icons-material";

const TranslatedContent: React.FunctionComponent<IConnectedTranslatedContentProps> = ({
    id,
    tenantSettings,
    lcidMappings,
    translatedContent,
    contentType,
    showSummary,
    customCss,
    getContentAnalysis,
    ...props
}) => {

    const useLanguagesData = useLanguages(translatedContent);
    const { activeLcid, defaultLcid } = useLanguagesData;
    const availableLanguages = useLanguagesData.availableLanguages ?? [];

    const [showContentAnalysis, setShowContentAnalysis] = React.useState<boolean>(false);

    const translateAllWrapper = async (inputTranslation: TranslatableContent, outputLanguages: ILcidTranslateDetails[]) => {
        return await cognitiveServicesApi.translateAllPostEventLanguages(
            id || defaultTranslateContentId,
            inputTranslation,
            outputLanguages
        )
    }

    const { isFetchingTranslations: isFetchingTranslation, errorModalStatus, translateAll } = useTranslateAll ({
        translatedContent: translatedContent ? translatedContent[defaultLcid] : DEFAULT_TRANSLATABLE_CONTENT,
        defaultLcid,
        availableLanguages,
        getAllTranslations: translateAllWrapper
    });

    const onChangeTranslatedContent = (updatedTranslatedContent: ITranslatedContent) => {
        props.onChange({ ...updatedTranslatedContent });
    }

    const requiredTranslationFieldsHasContent = (lcid: string): boolean => !!translatedContent && !!translatedContent[lcid].body && !!translatedContent[lcid].title;

    const translationHasContent = (lcid: string) =>
        !!translatedContent &&
        !!translatedContent[lcid] &&
        (!!translatedContent[lcid].title || !!translatedContent[lcid].body || !!translatedContent[lcid].description);

        
    const emptyTranslatedContent: ITranslatedContent = {
        [useLanguagesData.defaultLcid]: { body: "", description: "", title: "" },
    };

    const content: TranslatableContent = React.useMemo(
        () => (translatedContent?.[activeLcid] || { body: "", description: "", title: "" }),
        [translatedContent, activeLcid]
    );

    const getDraftTranslation = async (inputLanguage: string, outputLcid?: string) => {
        if (!inputLanguage || !translatedContent) return;

        const languageToTranslateTo = outputLcid ?? activeLcid;
        const outputLanguage = availableLanguages?.find((language: ILanguage) => language.lcid === languageToTranslateTo);

        if (outputLanguage) {
            try {
                const translation = await props.getDraftTranslation(id || defaultTranslateContentId, translatedContent![inputLanguage], outputLanguage.translateAs);
    
                props.onChange({...translatedContent, [outputLanguage.lcid]: {
                    ...translation,
                    body: `<p><em>[${lcidMappings[outputLanguage.lcid].autoTranslateText}]</em></p>${translation.body}`,
                }})
            }
            catch {
                let newTranslations = { ...translatedContent };

                props.onChange({...newTranslations,
                    [languageToTranslateTo]: {
                        ...DEFAULT_TRANSLATABLE_CONTENT,
                    }
                });
            }
        }
    };

    const onTranslateAll = async (outputLanguages: ILcidTranslateDetails[]) => {
        const finishedTranslations = await translateAll(outputLanguages);

        if (finishedTranslations) {
            //Apply translations after fetching
            let newTranslations = { ...translatedContent };
            
            outputLanguages.forEach(lang => { 
                const translation = finishedTranslations[lang.lcid];
                if (translation) {
                    newTranslations[lang.lcid] = {
                        ...translation,
                        body: `<p><em>[${lcidMappings[lang.lcid].autoTranslateText}]</em></p>${translation.body}`,
                    };
                }
            });
    
            onChangeTranslatedContent(newTranslations);
        }
    }

    const onChangeBody = (body: string) => {
        props.onChange({
            ...translatedContent,
            [activeLcid]: {
                ...translatedContent![activeLcid],
                body,
            },
        });
    };

    const onChangeSummary = (summary: string) => {
        props.onChange({
            ...translatedContent,
            [activeLcid]: {
                ...translatedContent![activeLcid],
                description: summary,
            },
        });
    };

    const onChangeTitle = (title: string) => {
        props.onChange({
            ...translatedContent,
            [activeLcid]: {
                ...translatedContent![activeLcid],
                title: title,
            },
        });
    };

    const onHideContentAnalysis = () => {
        setShowContentAnalysis(false);
    };

    const onRemoveSummary = () => {
        props.onRemoveSummary();
        onChangeSummary("");
    };

    const onShowContentAnalysis = () => {
        setShowContentAnalysis(true);
    };

    const getExtraLanguageBarButtons: () => JSX.Element = () => {
        const buttonDisabled = activeLcid !== "en-us" || !content.body

        return (
            <Button
                variant="text"
                color="primary"
                size="small"
                startIcon={<TimelineIcon />}
                onClick={onShowContentAnalysis}
                style={{marginLeft: "auto", marginRight: "20px"}}
                disabled={buttonDisabled}
            >
                Analyze Content
            </Button> 
        );
    }

    if (!lcidMappings || !tenantSettings) return null;

    return (
        <div>
            <div className="language-tabs">
                <div style={{width: "100%"}}>
                    <LoadingOverlay show={isFetchingTranslation}/>
                    <TranslateAllIssueDialog
                        show={errorModalStatus.showModal}
                        onClose={() => errorModalStatus.updateModalOpenStatus(false)}
                        totalLanguages={errorModalStatus.expectedLanguages}
                        returnedTranslations={errorModalStatus.actualLanguages}
                    />

                    {/* directly show the analyze button if language bar isn't being shown */}
                    {availableLanguages.length === 1 && 
                        getExtraLanguageBarButtons()
                    }

                    <LanguageBarWithTranslateDialog
                        translations={translatedContent ?? emptyTranslatedContent}
                        defaultContent={DEFAULT_TRANSLATABLE_CONTENT}
                        onChangeTranslatedContent={onChangeTranslatedContent}
                        onTranslate={getDraftTranslation}
                        endIcon={<KeyboardArrowDown />}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                        useLanguagesData={useLanguagesData}
                        requiredTranslationFieldsHasContent={requiredTranslationFieldsHasContent}
                        translationHasContent={translationHasContent}
                        contentType={TranslationContentType.Event}
                        showTranslateAllOption
                        translateAll={onTranslateAll}
                        translateAllDisabled={!translationHasContent(defaultLcid) || isFetchingTranslation}
                        disableLanguageButtons={isFetchingTranslation}
                        additionalButtons={getExtraLanguageBarButtons()}
                    />
                </div>
            </div>

            <Paper elevation={0} className="section-card">
                <div className="multilingual-editor">
                    <Title
                        title={content.title}
                        type={contentType}
                        onChange={onChangeTitle}
                    />
                    <Summary
                        show={showSummary}
                        summary={content.description}
                        onChange={onChangeSummary}
                        onRemove={onRemoveSummary}
                    />
                    <Body
                        customCss={customCss}
                        body={content.body}
                        lcid={activeLcid}
                        onChange={onChangeBody}
                    />
                </div>
            </Paper>
            <ContentAnalysis
                show={showContentAnalysis}
                id={id}
                body={content.body}
                getContentAnalysis={getContentAnalysis}
                onClose={onHideContentAnalysis}
            />
        </div>
    );
};

interface ITranslatedContentProps {
    showSummary: boolean;
    id: string | undefined;
    translatedContent: ITranslatedContent | undefined;
    customCss?: ICustomCssModelv1;
    contentType: "post" | "event";
    getContentAnalysis: (id: string, text: string) => Promise<IContentAnalysis>;
    getDraftTranslation: (id: string, content: TranslatableContent, outputLCID: string) => Promise<TranslatableContent>;
    onChange: (translatedContent: ITranslatedContent) => void;
    onRemoveSummary: () => void;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ITranslatedContentProps) => ({
        ...ownProps,
        lcidMappings: state.resources.lcidMappings,
        tenantSettings: state.settings.tenantSettings,
    }),
    {
    }
);
type IConnectedTranslatedContentProps = ConnectedProps<typeof connector>;

export default connector(TranslatedContent);
